import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {
  createHrProcessStep,
  deleteHrProcessStep,
  loadHrProcessSteps,
  updateHrProcessStep,
} from './Api'
import {
  buildFlowTree,
  collectSubtreeIds,
  END_STEP_NAME,
  HrProcessStepType,
  IHrProcessFlowNode,
  IHrProcessStepModel,
  NEW_STEP_NAME,
  toStep,
} from './HrProcessStepModel'
import {FlowHandlers, HrProcessFlowCanvas} from './HrProcessFlowNodes'
import {StepMailTemplateModal} from './StepMailTemplateModal'
import './HrProcessFlow.css'

type LocationState = {processName?: string} | undefined

const HrProcessFlowPage: FC = () => {
  const history = useHistory()
  const location = useLocation<LocationState>()
  const processId = useMemo(
    () => new URLSearchParams(location.search).get('processId')?.trim() || '',
    [location.search]
  )
  const processName = location.state?.processName

  const [steps, setSteps] = useState<IHrProcessStepModel[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [deletingNode, setDeletingNode] = useState<IHrProcessFlowNode | null>(null)
  const [mailNode, setMailNode] = useState<IHrProcessFlowNode | null>(null)

  const {roots, nodesById} = useMemo(() => buildFlowTree(steps), [steps])

  const fetchSteps = useCallback(async () => {
    const response = await loadHrProcessSteps(processId)
    if (response.data.hasError) throw new Error(response.data.message)
    setSteps(response.data.payload || [])
  }, [processId])

  useEffect(() => {
    if (!processId) return
    setLoading(true)
    fetchSteps()
      .catch(() => showErrorToast('Akış yüklenirken hata oluştu'))
      .finally(() => setLoading(false))
  }, [processId, fetchSteps])

  /** Her işlemden sonra (hata olsa bile) ekranı veritabanıyla eşitler */
  const runAction = async (action: () => Promise<void>, errorMessage: string) => {
    setBusy(true)
    try {
      await action()
    } catch (error) {
      showErrorToast(errorMessage)
    }
    try {
      await fetchSteps()
    } catch (error) {
      showErrorToast('Akış yenilenirken hata oluştu')
    } finally {
      setBusy(false)
    }
  }

  /** Tek alt adım varsa nextStepId = o adım; dallanmada veya alt adım yoksa boş */
  const syncNextStepId = async (parentId: string | null, childIds: string[]) => {
    const parent = parentId ? nodesById.get(parentId) : undefined
    if (!parent) return
    const nextStepId = childIds.length === 1 ? childIds[0] : null
    if (parent.nextStepId === nextStepId) return
    await updateHrProcessStep({...toStep(parent), nextStepId})
  }

  const createStep = async (preStepId: string | null, name: string, stepType: HrProcessStepType) => {
    const response = await createHrProcessStep({
      hrProcessId: processId,
      name,
      preStepId,
      nextStepId: null,
      stepType,
      // Yeni Bitiş adımı olumlu başlar; kullanıcı daire altından değiştirebilir
      outcome: stepType === 'END' ? 'POSITIVE' : null,
    })
    const id = response.data.payload?.id
    if (response.data.hasError || !id) throw new Error(response.data.message)
    return id
  }

  const addChildren = (
    parentId: string | null,
    names: string[],
    stepType: HrProcessStepType,
    errorMessage: string
  ) =>
    runAction(async () => {
      const ids: string[] = []
      // Sıralı oluştur: dalların soldan sağa sırası createdDate ile korunur
      for (const name of names) {
        ids.push(await createStep(parentId, name, stepType))
      }
      await syncNextStepId(parentId, ids)
    }, errorMessage)

  const handlers: FlowHandlers = {
    busy,
    onAdd: (parentId) => addChildren(parentId, [NEW_STEP_NAME], 'STEP', 'Adım eklenirken hata oluştu'),
    onSplit: (parentId) =>
      addChildren(
        parentId,
        [`${NEW_STEP_NAME} 1`, `${NEW_STEP_NAME} 2`],
        'STEP',
        'Dallanma eklenirken hata oluştu'
      ),
    onEnd: (parentId) => addChildren(parentId, [END_STEP_NAME], 'END', 'Bitiş eklenirken hata oluştu'),
    onRename: (node, name) =>
      runAction(async () => {
        await updateHrProcessStep({...toStep(node), name})
      }, 'Adım adı kaydedilemedi'),
    onDelete: setDeletingNode,
    onMailTemplate: setMailNode,
    onOutcome: (node, outcome) =>
      runAction(async () => {
        await updateHrProcessStep({...toStep(node), outcome})
      }, 'Bitiş sonucu kaydedilemedi'),
  }

  const handleMailTemplateSubmit = (node: IHrProcessFlowNode, mailTemplateId: string | null) => {
    setMailNode(null)
    runAction(async () => {
      await updateHrProcessStep({...toStep(node), mailTemplateId})
      showSuccessToast(mailTemplateId ? 'Adım maili kaydedildi' : 'Adım maili kaldırıldı')
    }, 'Adım maili kaydedilemedi')
  }

  const handleDelete = () => {
    const node = deletingNode
    setDeletingNode(null)
    if (!node) return
    runAction(async () => {
      for (const id of collectSubtreeIds(node)) {
        await deleteHrProcessStep(id)
      }
      const siblings = node.preStepId ? nodesById.get(node.preStepId)?.children ?? [] : []
      await syncNextStepId(
        node.preStepId,
        siblings.filter((sibling) => sibling.id !== node.id).map((sibling) => sibling.id)
      )
      showSuccessToast('Adım silindi')
    }, 'Adım silinirken hata oluştu')
  }

  const deleteText =
    deletingNode && deletingNode.children.length > 0
      ? `"${deletingNode.name}" adımı ve altındaki tüm adımlar silinecek. Emin misiniz?`
      : `"${deletingNode?.name ?? ''}" adımını silmek istediğinize emin misiniz?`

  return (
    <>
      <PageTitle breadcrumbs={[]}>İK Süreç Akışı</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>{processName || 'Süreç Akışı'}</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Adım adını değiştirmek için kutunun içine tıklayıp yazın; sol üstteki zarf ile adıma
              otomatik mail bağlayın
            </span>
          </h3>
          <div className='card-toolbar'>
            {busy && <span className='spinner-border spinner-border-sm text-primary me-4' />}
            <button type='button' className='btn btn-sm btn-light' onClick={() => history.goBack()}>
              <i className='bi bi-arrow-left fs-5'></i>
              Listeye Dön
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          {!processId ? (
            <div className='text-center text-muted py-10'>
              Süreç seçilmedi. Lütfen süreç listesinden "Akış" butonunu kullanın.
            </div>
          ) : loading ? (
            <div className='d-flex justify-content-center p-10'>
              <span className='spinner-border text-primary' role='status' />
            </div>
          ) : (
            <HrProcessFlowCanvas roots={roots} handlers={handlers} />
          )}
        </div>
      </div>

      <ConfirmModal
        show={!!deletingNode}
        header='Silme Onayı'
        text={deleteText}
        handleClose={() => setDeletingNode(null)}
        applyOnClick={handleDelete}
      />

      <StepMailTemplateModal
        node={mailNode}
        saving={busy}
        onClose={() => setMailNode(null)}
        onSubmit={handleMailTemplateSubmit}
      />

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {HrProcessFlowPage}
